import Phaser from "phaser";
import type { GridCamera } from "../world-zoom.ts";
import { NO_CHARACTERS, type CharacterSource } from "./characters.ts";
import { NO_THINGS, type ThingSource } from "./things.ts";
import { NO_GROUND, type GroundSource } from "./ground.ts";
import type { WorldUiContext, WorldUiModule, WorldUiSurface } from "./hook.ts";
import { projectToUi, type MutablePoint } from "./projection.ts";
import { NO_TARGET_SOURCE, type TargetSource } from "./target.ts";
import type { UiScaleSource } from "./ui-scale.ts";

/**
 * The two layers and the two cameras (ADR-061, "world-attached UI has a layer and a camera of its own").
 * World objects sit in one Phaser `Layer`, which the UI camera ignores, and world-attached UI in another,
 * which the world camera ignores. The world camera is the scene's main camera and follows the world zoom;
 * the UI camera, added after it so it draws on top, has its origin at the top left, no scroll and the UI
 * scale as its zoom, so one logical pixel is the same number of device pixels as in the DOM overlay. Both
 * cameras start at the game's size at (0, 0), so Phaser's camera manager resizes them with the game.
 *
 * The scene's factory is pointed at the world layer, so everything the scene and the renderer create with
 * `scene.add` (chunks, props, characters, debug views) lands there without knowing layers exist; surfaces
 * put their objects in the UI layer through their context. Phaser also adds to the scene's own display list
 * past the factory: a container gives a child it releases back to it, and `scene.make` with `add` true,
 * tilemap layers, groups and a bare `addToDisplayList()` add there directly. An object there sits in neither
 * layer, so both cameras would draw it; the mount moves every such object into the world layer as it arrives,
 * so the scene's display list holds the two layers and nothing else. A UI object a container releases
 * therefore lands in the world layer too, and the surface puts it back through its context.
 */

/**
 * The layers and the UI camera a mount made.
 */
export interface WorldUiMount {
  /** The layer the world camera draws, where the scene's factory adds. */
  readonly worldLayer: Phaser.GameObjects.Layer;
  /** The layer the UI camera draws. */
  readonly uiLayer: Phaser.GameObjects.Layer;
  /** The UI camera. */
  readonly camera: Phaser.Cameras.Scene2D.Camera;
}

/**
 * The part of Phaser's `GameObjectFactory` that decides where `scene.add` puts an object. Every factory
 * method adds through it, and a `Layer` takes the place of the scene's display list there: it keeps depth
 * sorting, and a container that takes a child from it removes the child from the layer.
 */
interface FactoryTarget {
  displayList: Phaser.GameObjects.DisplayList | Phaser.GameObjects.Layer;
}

/**
 * Queues what each surface loads. Call it from the scene's `preload`.
 *
 * @param load The scene's loader.
 * @param modules The surfaces.
 */
export function preloadWorldUi(load: Phaser.Loader.LoaderPlugin, modules: readonly WorldUiModule[]): void {
  for (const module of modules) {
    module.preload?.(load);
  }
}

/**
 * The world camera's view as it will render this frame: its scroll clamped to its bounds as
 * `Camera.preRender` clamps it.
 *
 * @param camera The world camera.
 * @param view Where to write the view.
 * @returns `view`.
 */
export function renderedView(camera: Phaser.Cameras.Scene2D.Camera, view: { -readonly [K in keyof GridCamera]: GridCamera[K] }): GridCamera {
  view.width = camera.width;
  view.height = camera.height;
  view.originX = camera.originX;
  view.originY = camera.originY;
  view.zoom = camera.zoom;
  view.scrollX = camera.useBounds ? camera.clampX(camera.scrollX) : camera.scrollX;
  view.scrollY = camera.useBounds ? camera.clampY(camera.scrollY) : camera.scrollY;
  return view;
}

/**
 * Makes the two layers and the UI camera, points the scene's factory at the world layer, moves into it
 * whatever arrives on the scene's own display list, and creates the surfaces. Call it first in the scene's `create`, before anything is added. Every frame after the scene's
 * `update` the surfaces update; at shutdown they are destroyed, the UI camera stops following the scale and
 * the factory is pointed back at the scene's display list, since Phaser sets the factory's target only once,
 * when the scene boots, and shutdown destroys the world layer. The mount also starts from the scene's display
 * list whatever the factory points at, so a scene that restarts mounts again.
 *
 * @param scene The scene.
 * @param scale The UI scale the camera follows.
 * @param modules The surfaces to create, in drawing order.
 * @param characters The characters the scene draws, which the surfaces read; none for a scene that draws none.
 * @param target The thing E acts on, which the interaction prompt names; none for a scene with no world to act on.
 * @param things The things the scene draws, which the damage numbers read; none for a scene that draws none.
 * @param ground The ground items the scene draws, which their labels read; none for a scene that draws none.
 * @returns The layers and the UI camera.
 * @throws Error when two surfaces share an id.
 */
export function mountWorldUi(scene: Phaser.Scene, scale: UiScaleSource, modules: readonly WorldUiModule[], characters: CharacterSource = NO_CHARACTERS, target: TargetSource = NO_TARGET_SOURCE, things: ThingSource = NO_THINGS, ground: GroundSource = NO_GROUND): WorldUiMount {
  const ids = new Set(modules.map((module) => module.id));
  if (ids.size !== modules.length) {
    throw new Error(`world UI surfaces repeat an id: ${modules.map((module) => module.id).join(", ")}`);
  }
  const root = scene.sys.displayList;
  const factory = scene.add as unknown as FactoryTarget;
  factory.displayList = root;
  const worldLayer = scene.add.layer().setDepth(0);
  const uiLayer = scene.add.layer().setDepth(1);
  factory.displayList = worldLayer;
  const world = scene.cameras.main;
  const camera = scene.cameras.add(0, 0, scene.scale.width, scene.scale.height, false, "ui");
  camera.setOrigin(0, 0).setScroll(0, 0).setZoom(scale.current).setRoundPixels(true);
  world.ignore(uiLayer);
  camera.ignore(worldLayer);

  /**
   * Moves an object that arrived on the scene's own display list into the world layer. Phaser emits the event
   * after the object is on the list, so the move takes it off again; the layer's own event for the move is
   * then one this listener ignores. It stops listening when the world layer is destroyed, which at shutdown
   * is before the layer's children are.
   *
   * @param object The object just added to a list in the scene.
   */
  const onAdded = (object: Phaser.GameObjects.GameObject): void => {
    if (object.displayList === root && object !== worldLayer && object !== uiLayer) {
      worldLayer.add(object);
    }
  };
  scene.events.on(Phaser.Scenes.Events.ADDED_TO_SCENE, onAdded);
  worldLayer.once(Phaser.GameObjects.Events.DESTROY, () => {
    scene.events.off(Phaser.Scenes.Events.ADDED_TO_SCENE, onAdded);
  });
  const stopScale = scale.subscribe((next) => {
    camera.setZoom(next);
  });
  const view = { width: 0, height: 0, originX: 0, originY: 0, zoom: 1, scrollX: 0, scrollY: 0 };
  const context: WorldUiContext = {
    scene,
    layer: uiLayer,
    camera,
    scale,
    worldLayer,
    worldCamera: world,
    characters,
    target,
    things,
    ground,
    add: (object) => {
      uiLayer.add(object);
      return object;
    },
    project: (worldX: number, worldY: number, out: MutablePoint) => projectToUi(renderedView(world, view), scale.current, worldX, worldY, out),
  };
  const surfaces: WorldUiSurface[] = modules.map((module) => module.create(context));

  /**
   * Updates every surface after the scene's own update has placed the world camera.
   *
   * @param time Scene time in ms.
   * @param delta Frame delta in ms.
   */
  const onPostUpdate = (time: number, delta: number): void => {
    for (let index = 0; index < surfaces.length; index += 1) {
      (surfaces[index] as WorldUiSurface).update?.(time, delta);
    }
  };
  scene.events.on(Phaser.Scenes.Events.POST_UPDATE, onPostUpdate);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    factory.displayList = root;
    scene.events.off(Phaser.Scenes.Events.POST_UPDATE, onPostUpdate);
    stopScale();
    for (const surface of surfaces) {
      surface.destroy?.();
    }
  });
  return { worldLayer, uiLayer, camera };
}
